import { Menu, Moon, Sun, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/shell/ErrorBoundary';
import { TopProgress } from '@/components/shell/TopProgress';
import { Button } from '@/components/ui';
import { clearAccount, useAccount } from '@/lib/account';
import { ACCESS_ERROR_EVENT, api } from '@/lib/api/client';
import { useMe } from '@/lib/api/queries';
import { preloadCatalogs } from '@/lib/db/preload';
import { LANGUAGES, setLanguage } from '@/lib/i18n';
import { ACCOUNT_ENTRIES, GAME_SECTIONS, sectionForPath } from '@/lib/menu';
import { cn } from '@/lib/utils';

/**
 * The frame every page renders inside.
 *
 * It owns three things no page should have to: which game accent is active, where an
 * EULA or ban redirect goes, and when the static catalogs start downloading.
 */
export function AppShell() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const account = useAccount();
  const { data: user } = useMe();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));

  const section = sectionForPath(location.pathname);

  /**
   * EULA and ban redirects.
   *
   * The api client cannot navigate — it has no router — so it dispatches an event and
   * this decides what to do with it. Guarding on the current path matters: without it,
   * the request the `/eula` page itself makes would bounce it to `/eula` forever.
   */
  useEffect(() => {
    function onAccessError(event: Event) {
      const reason = (event as CustomEvent<string>).detail;
      const target =
        reason === 'ACCOUNT_BANNED'
          ? '/banned'
          : reason === 'PASSWORD_CHANGE_REQUIRED'
            ? '/change-password'
            : '/eula';
      if (location.pathname !== target) navigate(target);
    }
    window.addEventListener(ACCESS_ERROR_EVENT, onAccessError);
    return () => window.removeEventListener(ACCESS_ERROR_EVENT, onAccessError);
  }, [location.pathname, navigate]);

  /**
   * The same two redirects, driven by `/api/user/me` instead of by a failure.
   *
   * The event above only fires when a request is refused, and the dashboard's only
   * request is `/api/user/me` — which stays open to a banned or locked account by
   * design. Without this, such an account lands on a dashboard that looks entirely
   * normal and is told nothing until it happens to click a page that does get refused.
   */
  const restriction = user?.restriction ?? null;
  useEffect(() => {
    if (!restriction) return;
    const target = restriction === 'banned' ? '/banned' : '/change-password';
    if (location.pathname !== target) navigate(target, { replace: true });
  }, [restriction, location.pathname, navigate]);

  // Catalogs are only worth downloading for someone who can see a game page.
  useEffect(() => {
    if (account) void preloadCatalogs();
  }, [account]);

  function toggleTheme() {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    try {
      localStorage.setItem('theme', next ? 'dark' : 'light');
    } catch {
      // Storage denied; the choice still applies until reload.
    }
  }

  async function signOut() {
    const refreshToken = account?.refreshToken;
    // Best effort: the session is gone locally either way, and a failed revoke must
    // not leave the user staring at a page they think they are signed out of.
    if (refreshToken) {
      await api.post('api/auth/signout', { refreshToken }).catch(() => undefined);
    }
    clearAccount();
    navigate('/');
  }

  const games = user?.games ?? [];
  const visibleSections = GAME_SECTIONS.filter((s) => games.includes(s.key));

  return (
    <div data-game={section?.game} className="min-h-dvh">
      <TopProgress />

      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4">
          <Button
            variant="ghost"
            className="px-2 md:hidden"
            aria-label="Menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>

          <Link to="/" className="font-semibold tracking-tight">
            QuiltNET
          </Link>

          <div className="ml-auto flex items-center gap-1">
            <select
              aria-label={t('Nav.Language')}
              value={i18n.language.split('-')[0]}
              onChange={(event) => setLanguage(event.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm"
            >
              {LANGUAGES.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.label}
                </option>
              ))}
            </select>

            <Button
              variant="ghost"
              className="px-2"
              aria-label={t('Nav.Theme')}
              onClick={toggleTheme}
            >
              {dark ? <Sun className="size-5" /> : <Moon className="size-5" />}
            </Button>

            {account ? (
              <Button variant="ghost" onClick={() => void signOut()}>
                {t('Nav.SignOut')}
              </Button>
            ) : (
              <Button onClick={() => navigate('/sign-in')}>{t('Nav.SignIn')}</Button>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-6 px-4 py-6">
        {account && (
          <nav
            className={cn(
              'w-56 shrink-0 md:block',
              menuOpen
                ? 'fixed inset-x-0 bottom-0 top-14 z-30 w-full overflow-y-auto border-r border-border bg-background p-4 md:static md:inset-auto md:w-56 md:p-0'
                : 'hidden',
            )}
          >
            <NavGroup
              title={t('Nav.Account')}
              entries={ACCOUNT_ENTRIES}
              onNavigate={() => setMenuOpen(false)}
            />
            {visibleSections.map((gameSection) => (
              <NavGroup
                key={gameSection.key}
                title={t(gameSection.title)}
                entries={gameSection.entries}
                onNavigate={() => setMenuOpen(false)}
              />
            ))}
          </nav>
        )}

        <main className="min-w-0 flex-1">
          {/* Keyed on the path so navigating away clears a crash. */}
          <ErrorBoundary
            resetKey={location.pathname}
            title={t('Error.PageTitle')}
            hint={t('Error.PageHint')}
            retryLabel={t('Error.Retry')}
          >
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

function NavGroup({
  title,
  entries,
  onNavigate,
}: {
  title: string;
  entries: readonly { to: string; title: string }[];
  /** Closes the mobile drawer, which would otherwise cover the page it just opened. */
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-6">
      <div className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <ul>
        {entries.map((entry) => (
          <li key={entry.to}>
            <NavLink
              to={entry.to}
              end
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'block rounded-md px-3 py-1.5 text-sm',
                  isActive
                    ? 'bg-secondary font-medium text-accent'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                )
              }
            >
              {t(entry.title)}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
